import { useEffect, useState } from "react";
import { api } from "../../../helpers/api";
import { ShowToast } from "../../../components/toast";
import { totpErrorMessage } from "./totp-error";

export function EnrollTotpModal({
  onEnrolled,
  onClose,
}: {
  onEnrolled: () => void;
  onClose: () => void;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    api.chargeAuth
      .enrollStart()
      .then((r) => active && setQr(r.qrDataUri))
      .catch((e) => active && setError(totpErrorMessage(e).message));
    return () => {
      active = false;
    };
  }, []);

  const confirm = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api.chargeAuth.enrollConfirm(code);
      ShowToast({ type: "success", message: "Authenticator set up" });
      onEnrolled();
    } catch (e) {
      setError(totpErrorMessage(e).message);
    } finally {
      setBusy(false);
    }
  };

  const valid = /^\d{6}$/.test(code);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full">
        <h3 className="text-lg font-semibold mb-2">Set up your authenticator</h3>
        <p className="text-sm text-gray-500 mb-4">
          Scan this with Google Authenticator, then enter the 6-digit code to
          finish.
        </p>
        {qr ? (
          <img
            src={qr}
            alt="Authenticator QR"
            className="w-40 h-40 mx-auto mb-4"
          />
        ) : (
          <div className="w-40 h-40 mx-auto mb-4 bg-gray-100 animate-pulse rounded" />
        )}
        <input
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
          className="w-full px-3 py-2 text-center text-2xl tracking-widest border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        <div className="flex gap-3 justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md bg-gray-100 text-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!valid || busy}
            className="px-4 py-2 text-sm rounded-md bg-black text-white font-semibold disabled:opacity-50"
          >
            {busy ? "Verifying..." : "Finish setup"}
          </button>
        </div>
      </div>
    </div>
  );
}
